import { inject } from '@angular/core';
import {
    HttpEvent,
    HttpInterceptorFn,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { EnvironmentService } from '@lib/shared/data-access';
import { AuthService } from '@lib/auth/data-access';

/**
 * An HttpInterceptorFn that automatically adds the current tenant's external ID
 * to outgoing API requests that require it.
 */
export const tenantInterceptor: HttpInterceptorFn = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const environmentService = inject(EnvironmentService);   

    // --- Logic to determine if this URL needs the tenant ID ---
    const isApiRequest = req.url.startsWith(environmentService.pathfinderApiUrl) ||
                        req.url.startsWith(environmentService.digitalMediaApiUrl) ||
                        req.url.startsWith(environmentService.reportingApiUrl);

    const isTenantlessRoute = req.url.includes(environmentService.authApiUrl) ||
                              req.url.includes('/api/v1/t/') || // already has tenant in URL
                              req.url.includes('/version/build-version');
                              
    const isAlreadyPointingToATenant = /\/t\/[0-9a-fA-F-]{36}\//.test(req.url); // Checks for /t/{guid}/ in the URL

    if (isApiRequest && !isTenantlessRoute && !isAlreadyPointingToATenant) {
        // Get the current tenant's external ID from your session service
        const currentTenantExternalId = authService.userPrincipalComputedSignal()?.currentTenantExternalId ?? null;

        if (currentTenantExternalId) {
            // --- Modify the URL ---
            // Example: /api/v1/advanced-search/...
            // Becomes: /api/v1/t/{guid}/advanced-search/...

            // Find the position after '/api/vX/'
            const apiVersionRegex = /(\/api\/v\d+(\.\d+)?\/)/;
            const match = req.url.match(apiVersionRegex);

            if (match && match.index !== undefined) {
                const insertPosition = match.index + match[0].length;
                const newUrl = `${req.url.slice(0, insertPosition)}t/${currentTenantExternalId}/${req.url.slice(insertPosition)}`;

                // Clone the request with the modified URL
                const clonedReq = req.clone({ url: newUrl });
                return next(clonedReq);
            } else {
                // Log a warning if the URL format wasn't as expected, but proceed
                console.warn(`TenantInterceptor: Could not inject tenant ID into URL: ${req.url}. URL format might be unexpected.`);
            }
        } else {
             // If the route requires a tenant but we don't have one, maybe redirect or log?
             // This might indicate an issue if the resolver didn't run or failed.
             console.warn(`TenantInterceptor: No tenantExternalId found in session context for tenant-specific route: ${req.url}`);
        }
    }

    // If it's not an API request needing the tenant ID, pass it through unchanged
    return next(req);
};
